// Page Attestation - V8 — Refonte responsive 150% + dédoublonnage
// Attestation directe (1-4 casques) + workflow badge doré (5 casques + certification)

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { SEUILS_CASQUES } from '@autodiag/shared'
import { api } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useEntreprise } from '../contexts/EntrepriseContext'
import { useNotifications } from '../contexts/NotificationsContext'
import ResultsSummaryCard from '../components/results/ResultsSummaryCard'

// Labels selon niveau de casques (1-5)
function getNiveauLabel(casques: number) {
  switch (casques) {
    case 1: return 'Engagé en prévention'
    case 2: return 'Impliqué en prévention'
    case 3: return 'Performant en prévention'
    case 4: return 'Expert en prévention'
    case 5: return 'Excellence en prévention'
    default: return 'Engagé en prévention'
  }
}

interface AttestationData {
  genre: 'M' | 'Mme' | ''
  nomGerant: string
  prenomGerant: string
  nomEntreprise: string
  siret: string
  dateDiagnostic: string
  metiers: string[]
  casques: number
}

interface CertificatUpload {
  file: File | null
  fileName: string
  email: string
  commentaire: string
  certificationType: 'MASE' | 'ISO' | 'CPP' | ''
}

export default function AttestationPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { user } = useAuth()
  const { currentEntreprise, currentCompany } = useEntreprise()
  const { refreshNotifications } = useNotifications()

  // Charger les données du diagnostic (depuis state ou IndexedDB)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [isLoading, setIsLoading] = useState(true)
  const [casques, setCasques] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [pdfError, setPdfError] = useState('')
  const [duerpBlocked, setDuerpBlocked] = useState(false)

  // States pour les scores (card enrichie)
  const [scoreGlobal, setScoreGlobal] = useState(0)
  const [maxGlobal, setMaxGlobal] = useState(0)
  const [scoreQ1, setScoreQ1] = useState(0)
  const [maxQ1, setMaxQ1] = useState(0)
  const [scoreQ2, setScoreQ2] = useState(0)
  const [maxQ2, setMaxQ2] = useState(0)

  // Formulaire attestation — pre-rempli depuis SSO
  const [formData, setFormData] = useState<AttestationData>(() => ({
    genre: '',
    nomGerant: user?.nom || '',
    prenomGerant: user?.prenom || '',
    nomEntreprise: '',
    siret: '',
    dateDiagnostic: new Date().toISOString().split('T')[0],
    metiers: [],
    casques: 1,
  }))

  // Formulaire upload certificat — email pre-rempli depuis SSO
  const [certificat, setCertificat] = useState<CertificatUpload>(() => ({
    file: null,
    fileName: '',
    email: user?.email || '',
    commentaire: '',
    certificationType: '',
  }))

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Générer les étapes de progression
  const steps = useDiagnosticSteps('attestation', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Charger les données du diagnostic
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)
      try {
        // Mettre à jour le formulaire avec les métiers
        if (metiersSelectionnes.length > 0) {
          setFormData(prev => ({
            ...prev,
            metiers: metiersSelectionnes.map(m => m.nom),
          }))
        }

        // Charger le diagnostic complet depuis l'API
        let diagnostic = await api.getDiagnostic(diagnosticId)

        // Filet de sécurité : si pas de resultat, calculer les scores serveur
        // (peut arriver si l'utilisateur a navigué via le stepper au lieu du bouton Q2)
        if (!diagnostic.resultat && (diagnostic.reponses || []).length > 0) {
          try {
            await api.terminerDiagnostic(diagnosticId)
            diagnostic = await api.getDiagnostic(diagnosticId)
          } catch {
            // Pas bloquant — on essaiera avec les données disponibles
          }
        }

        if (diagnostic?.dateDebut || diagnostic?.createdAt) {
          const dateStr = diagnostic.dateDebut || diagnostic.createdAt
          setFormData(prev => ({
            ...prev,
            dateDiagnostic: new Date(dateStr).toISOString().split('T')[0],
          }))
        }

        // Pre-remplir l'entreprise et le SIRET depuis le diagnostic ou le contexte SSO
        const entrepriseNom = diagnostic?.entreprise?.raisonSociale || currentEntreprise || ''
        const entrepriseSiret = diagnostic?.entreprise?.siret || currentCompany?.siret || ''
        if (entrepriseNom) {
          setFormData(prev => ({
            ...prev,
            nomEntreprise: prev.nomEntreprise || entrepriseNom,
            siret: prev.siret || entrepriseSiret,
          }))
        }

        // Verifier DUERP (Q2-03) — "Non fait" (valeur 0) bloque l'attestation
        const duerpReponse = diagnostic.reponses?.find(
          (r: any) => r.question?.code === 'Q2-03'
        )
        setDuerpBlocked(!duerpReponse || duerpReponse.valeur === 0)

        // Charger le résultat (casques + scores) depuis le diagnostic
        const resultat = diagnostic.resultat
        if (resultat) {
          setCasques(resultat.casques || 1)
          setFormData(prev => ({ ...prev, casques: resultat.casques || 1 }))

          // Charger les scores pour la card enrichie
          setScoreGlobal(resultat.scoreGlobal || 0)
          setMaxGlobal(resultat.maxGlobal || 0)
          setScoreQ2(resultat.scoreQ2 || 0)
          setMaxQ2(resultat.maxQ2 || 0)

          // Calculer Q1 depuis scoresParMetier (ou scoresMetier)
          const scoresParMetier = resultat.scoresParMetier || resultat.scoresMetier || []
          if (scoresParMetier.length > 0) {
            const q1Score = scoresParMetier.reduce((acc: number, m: any) => acc + (m.scoreQ1 || 0), 0)
            const q1Max = scoresParMetier.reduce((acc: number, m: any) => acc + (m.maxQ1 || 0), 0)
            setScoreQ1(q1Score)
            setMaxQ1(q1Max)
          }
        }
      } catch (err) {
        console.error('Erreur chargement données:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId, metiersSelectionnes, currentEntreprise])

  // Handlers formulaire
  const handleInputChange = (field: keyof AttestationData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleCertificatChange = (field: keyof CertificatUpload, value: string | File | null) => {
    setCertificat(prev => ({ ...prev, [field]: value }))
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCertificat(prev => ({
        ...prev,
        file,
        fileName: file.name,
      }))
    }
  }

  const handleGeneratePdf = async () => {
    if (!diagnosticId || !isFormValid) return
    trackEvent('generer pdf attestation')
    setGeneratingPdf(true)
    setPdfError('')
    try {
      // Créer l'attestation en base si pas encore fait (1-4 casques)
      if (!needsValidation) {
        await api.createDirectAttestation({
          diagnosticId,
          genreGerant: formData.genre as 'M' | 'Mme',
          nomGerant: formData.nomGerant,
          prenomGerant: formData.prenomGerant,
          nomEntreprise: formData.nomEntreprise,
        })
      }
      // Télécharger le PDF
      await api.downloadAttestationPDF(diagnosticId)
      refreshNotifications()
    } catch (err: any) {
      console.error('Erreur génération PDF:', err)
      setPdfError(err?.message || 'Erreur lors de la génération du PDF')
    } finally {
      setGeneratingPdf(false)
    }
  }

  const handleSubmitCertificat = async () => {
    if (!certificat.file || !certificat.email || !certificat.certificationType || !diagnosticId) return
    trackEvent('envoyer demande certificat')

    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('certificat', certificat.file)
      fd.append('diagnosticId', diagnosticId)
      fd.append('genreGerant', formData.genre)
      fd.append('nomGerant', formData.nomGerant)
      fd.append('prenomGerant', formData.prenomGerant)
      fd.append('nomEntreprise', formData.nomEntreprise)
      fd.append('emailContact', certificat.email)
      fd.append('certificationType', certificat.certificationType)
      if (certificat.commentaire) fd.append('commentaire', certificat.commentaire)

      await api.createAttestation(fd)
      setSubmitted(true)
      refreshNotifications()
    } catch (err) {
      console.error('Erreur envoi certificat:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Navigation
  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/actions`, {
      state: diagnosticState,
    })
  }

  const handleReturnHome = () => {
    navigate('/')
  }

  // 5 casques bruts : afficher 4 par défaut, workflow 5 casques si l'utilisateur choisit de soumettre une certification
  const rawCasques = casques
  const casquesAffiches = casques === 5 ? 4 : casques
  const eligible5eme = rawCasques === 5
  const [wantsCertification, setWantsCertification] = useState(false)
  const needsValidation = eligible5eme && wantsCertification

  // Durée de validité : 1 an (seul le 5ème casque validé par admin donne 2 ans)
  const dureeAnnees = 1
  const dateValidite = new Date(formData.dateDiagnostic)
  dateValidite.setFullYear(dateValidite.getFullYear() + dureeAnnees)
  const dateValiditeStr = dateValidite.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  // Calcul des seuils pour ScoreProgressBar
  const nbMetiers = Math.min(3, Math.max(1, metierIds.length || 1)) as 1 | 2 | 3
  const seuilsActuels = SEUILS_CASQUES[nbMetiers]

  // Validation formulaire (genre + nom + entreprise obligatoires + DUERP non bloque)
  const isFormValid = !duerpBlocked && formData.genre !== '' && formData.nomGerant.trim() !== '' && formData.nomEntreprise.trim() !== ''
  const isCertificatFormValid = isFormValid && certificat.file !== null && certificat.email.includes('@') && certificat.certificationType !== ''

  // Indicateur visuel pour les champs pre-remplis depuis le SSO
  const SsoHint = () => (
    <span className="text-[10px] text-muted-foreground/60 ml-1" title="Pre-rempli depuis votre compte">
      (compte)
    </span>
  )

  // Écran de chargement
  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  // Écran de confirmation (5 casques, demande soumise)
  if (submitted) {
    return (
      <DiagnosticLayout
        steps={steps}
        onBack={handleBack}
        backLabel="Plan d'actions"
        diagnosticState={diagnosticState}
      >
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Demande envoyée !
            </h1>
            <p className="text-primary/80 mb-6 max-w-md mx-auto">
              Votre demande d'attestation {casques} casques a été transmise à l'OPPBTP.
              Vous recevrez une notification par email à l'adresse <strong>{certificat.email}</strong> une fois votre demande traitée.
            </p>
            <div className="bg-white rounded-xl p-6 max-w-md mx-auto mb-6">
              <h3 className="font-semibold text-foreground mb-3">Récapitulatif</h3>
              <div className="text-left text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Certificat</span>
                  <span className="text-foreground">{certificat.fileName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="text-foreground">{certificat.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Casques demandés</span>
                  <span className="text-foreground">{casques} casques</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleReturnHome}
              className="px-6 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              Retour à l'accueil
            </button>
          </CardContent>
        </Card>
      </DiagnosticLayout>
    )
  }

  // ========== LAYOUT UNIFIÉ (1-5 casques) ==========
  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      backLabel="Plan d'actions"
      diagnosticState={diagnosticState}
    >
      {/* Banner DUERP bloquant */}
      {duerpBlocked && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-destructive">
                Attestation non disponible
              </h2>
              <p className="text-sm text-destructive/80 mt-1">
                Le DUERP (Document Unique d'Évaluation des Risques Professionnels) doit être au minimum planifié pour pouvoir générer l'attestation. Veuillez modifier votre réponse à la question DUERP dans le questionnaire Management.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner de félicitations */}
      {!duerpBlocked && (
      <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {needsValidation ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              )}
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground">
              Félicitations ! {casquesAffiches} casque{casquesAffiches > 1 ? 's' : ''} obtenu{casquesAffiches > 1 ? 's' : ''}
            </h1>
            <p className="text-sm text-muted-foreground">
              Renseignez les informations pour générer votre attestation officielle
            </p>
            {eligible5eme && (
              <p className="text-sm text-primary mt-1">
                Vous pouvez prétendre au 5e casque avec une certification MASE, ISO 45001 ou CPP.
              </p>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Layout principal : Card Résultats (2/3) + Panel latéral (1/3) */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${needsValidation ? 'mb-6' : ''}`}>
        {/* Card Résultats Enrichie - 2/3 */}
        <div className="lg:col-span-2">
          <ResultsSummaryCard
            casques={casquesAffiches}
            metiersSelectionnes={metiersSelectionnes}
            metierIds={metierIds}
            scoreGlobal={scoreGlobal}
            maxGlobal={maxGlobal}
            scoreQ1={scoreQ1}
            maxQ1={maxQ1}
            scoreQ2={scoreQ2}
            maxQ2={maxQ2}
            seuilsActuels={seuilsActuels}
            getNiveauLabel={getNiveauLabel}
          />
        </div>

        {/* Panel latéral - 1/3 */}
        <div className="lg:col-span-1">
          {needsValidation ? (
            /* Processus de validation (5 casques) */
            <Card className="h-full">
              <CardContent className="p-5">
                <h2 className="font-semibold text-foreground mb-4 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Processus de validation
                </h2>
                <div className="space-y-3">
                  {[
                    { num: 1, title: 'Déposer', desc: 'Certificat MASE/ISO', active: true },
                    { num: 2, title: 'Renseigner', desc: 'Email de contact', active: true },
                    { num: 3, title: 'Validation', desc: 'Examen OPPBTP (5j)', active: false },
                    { num: 4, title: 'Attestation', desc: 'Réception par email', active: false },
                  ].map((step) => (
                    <div
                      key={step.num}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        step.active ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                        step.active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {step.num}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${step.active ? 'text-primary' : 'text-muted-foreground'}`}>
                          {step.title}
                        </div>
                        <div className={`text-xs ${step.active ? 'text-primary/80' : 'text-muted-foreground'}`}>
                          {step.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Formulaire attestation directe (1-4 casques) */
            <Card className="h-full">
              <CardContent className="p-5 flex flex-col h-full">
                <h2 className="font-semibold text-foreground mb-4 text-sm">Informations attestation</h2>

                <div className="space-y-4 flex-1">
                  {/* Logo entreprise (depuis SSO) */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Logo entreprise
                    </label>
                    {user?.pictureUrl ? (
                      <div className="w-full h-20 bg-muted/30 rounded-lg flex items-center justify-center border border-border">
                        <img
                          src={user.pictureUrl}
                          alt="Logo entreprise"
                          className="max-h-16 max-w-full object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-20 bg-muted/50 rounded-lg flex items-center justify-center border-2 border-dashed border-border">
                        <span className="text-xs text-muted-foreground">Non disponible</span>
                      </div>
                    )}
                  </div>

                  {/* Genre */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Genre *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="genre"
                          value="M"
                          checked={formData.genre === 'M'}
                          onChange={(e) => handleInputChange('genre', e.target.value)}
                          className="w-4 h-4 text-primary border-border focus:ring-primary"
                        />
                        <span className="text-sm text-muted-foreground">M.</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="genre"
                          value="Mme"
                          checked={formData.genre === 'Mme'}
                          onChange={(e) => handleInputChange('genre', e.target.value)}
                          className="w-4 h-4 text-primary border-border focus:ring-primary"
                        />
                        <span className="text-sm text-muted-foreground">Mme</span>
                      </label>
                    </div>
                  </div>

                  {/* Prénom */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Prenom * {user?.prenom && <SsoHint />}
                    </label>
                    <input
                      type="text"
                      value={formData.prenomGerant}
                      onChange={(e) => handleInputChange('prenomGerant', e.target.value)}
                      placeholder="Prenom"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>

                  {/* Nom */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Nom * {user?.nom && <SsoHint />}
                    </label>
                    <input
                      type="text"
                      value={formData.nomGerant}
                      onChange={(e) => handleInputChange('nomGerant', e.target.value)}
                      placeholder="Nom"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>

                  {/* Entreprise */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Entreprise * {formData.nomEntreprise && <SsoHint />}
                    </label>
                    <input
                      type="text"
                      value={formData.nomEntreprise}
                      onChange={(e) => handleInputChange('nomEntreprise', e.target.value)}
                      placeholder="Raison sociale"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>

                  {/* Infos du diagnostic (lecture seule) */}
                  <div className="pt-3 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Infos du diagnostic</p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date</span>
                        <span className="text-foreground font-medium">
                          {new Date(formData.dateDiagnostic).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Validité</span>
                        <span className="text-foreground font-medium">{dateValiditeStr}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Métiers</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {metiersSelectionnes.map((m) => (
                            <span
                              key={m.code}
                              className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs"
                            >
                              {m.nom || m.code}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Boutons action */}
                <div className="mt-4 space-y-2">
                  {/* Bouton principal : Générer le PDF */}
                  <button
                    onClick={handleGeneratePdf}
                    disabled={!isFormValid || generatingPdf}
                    className={`w-full px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      isFormValid && !generatingPdf
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-muted text-muted-foreground/60 cursor-not-allowed'
                    }`}
                  >
                    {generatingPdf ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {eligible5eme ? 'Attestation 4 casques' : 'Générer le PDF'}
                      </>
                    )}
                  </button>

                  {/* Bouton secondaire : Obtenir le 5ème casque (seulement si éligible) */}
                  {eligible5eme && (
                    <button
                      onClick={() => {
                        trackEvent('demander 5eme casque')
                        setWantsCertification(true)
                      }}
                      className="w-full px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center gap-2 border border-primary text-primary hover:bg-primary/5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      Obtenir le 5ème casque
                    </button>
                  )}

                  {pdfError && (
                    <p className="text-xs text-destructive text-center">{pdfError}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Sections supplémentaires pour 5 casques (workflow validation) */}
      {needsValidation && (
        <>
          {/* Bouton retour vers attestation 4 casques */}
          <button
            onClick={() => setWantsCertification(false)}
            className="mb-4 text-sm text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Revenir à l'attestation 4 casques
          </button>

          {/* Informations attestation (gerant + entreprise) */}
          <Card className="mb-6">
            <CardContent className="py-6">
              <h2 className="font-semibold text-foreground mb-1">Informations attestation</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Ces informations figureront sur votre attestation officielle
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Genre */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Genre *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="genre-5c"
                        value="M"
                        checked={formData.genre === 'M'}
                        onChange={(e) => handleInputChange('genre', e.target.value)}
                        className="w-4 h-4 text-primary border-border focus:ring-primary"
                      />
                      <span className="text-sm text-muted-foreground">M.</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="genre-5c"
                        value="Mme"
                        checked={formData.genre === 'Mme'}
                        onChange={(e) => handleInputChange('genre', e.target.value)}
                        className="w-4 h-4 text-primary border-border focus:ring-primary"
                      />
                      <span className="text-sm text-muted-foreground">Mme</span>
                    </label>
                  </div>
                </div>

                {/* Prenom */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Prenom * {user?.prenom && <SsoHint />}
                  </label>
                  <input
                    type="text"
                    value={formData.prenomGerant}
                    onChange={(e) => handleInputChange('prenomGerant', e.target.value)}
                    placeholder="Prenom"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Nom * {user?.nom && <SsoHint />}
                  </label>
                  <input
                    type="text"
                    value={formData.nomGerant}
                    onChange={(e) => handleInputChange('nomGerant', e.target.value)}
                    placeholder="Nom"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                {/* Entreprise */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Entreprise * {formData.nomEntreprise && <SsoHint />}
                  </label>
                  <input
                    type="text"
                    value={formData.nomEntreprise}
                    onChange={(e) => handleInputChange('nomEntreprise', e.target.value)}
                    placeholder="Raison sociale"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                </div>

                {/* SIRET */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    SIRET {currentCompany?.siret && <SsoHint />}
                  </label>
                  <input
                    type="text"
                    value={formData.siret}
                    onChange={(e) => handleInputChange('siret', e.target.value)}
                    placeholder="N° SIRET (14 chiffres)"
                    maxLength={14}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents de justification */}
          <Card className="mb-6">
            <CardContent className="py-6">
              <h2 className="font-semibold text-foreground mb-6">Documents de justification</h2>

              {/* Upload fichier */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Fichier justificatif *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    certificat.file
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-border hover:border-primary/40 hover:bg-primary/5'
                  }`}
                >
                  {certificat.file ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-foreground">{certificat.fileName}</div>
                        <div className="text-sm text-primary">Cliquez pour changer de fichier</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <svg className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <div className="text-muted-foreground font-medium">Cliquez pour sélectionner un fichier</div>
                      <div className="text-sm text-muted-foreground mt-1">PDF, JPG ou PNG (max 10 Mo)</div>
                    </>
                  )}
                </div>
              </div>

              {/* Type de justification */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Type de justification *
                </label>
                <div className="flex gap-4">
                  {(['CPP', 'MASE', 'ISO'] as const).map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer px-4 py-2.5 border border-border rounded-xl hover:bg-muted/50 transition-all has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <input
                        type="radio"
                        name="certificationType"
                        value={type}
                        checked={certificat.certificationType === type}
                        onChange={(e) => handleCertificatChange('certificationType', e.target.value)}
                        className="accent-primary"
                      />
                      <span className="font-medium">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Email de contact * {user?.email && <SsoHint />}
                </label>
                <input
                  type="email"
                  value={certificat.email}
                  onChange={(e) => handleCertificatChange('email', e.target.value)}
                  placeholder="votre@email.com"
                  className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                />
                <p className="text-sm text-muted-foreground mt-1.5">
                  Vous recevrez une notification lorsque votre demande sera traitée.
                </p>
              </div>

              {/* Commentaire (optionnel) */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  value={certificat.commentaire}
                  onChange={(e) => handleCertificatChange('commentaire', e.target.value)}
                  placeholder="Informations complémentaires..."
                  rows={3}
                  className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                />
              </div>

              {/* Bouton submit */}
              <button
                onClick={handleSubmitCertificat}
                disabled={!isCertificatFormValid || submitting}
                className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  isCertificatFormValid && !submitting
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Envoi en cours...
                  </>
                ) : (
                  <>Envoyer ma demande</>
                )}
              </button>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-700">
                La validation de votre demande est effectuée par l'équipe OPPBTP. Le délai de traitement est généralement de 5 jours ouvrés.
              </p>
            </div>
          </div>
        </>
      )}
    </DiagnosticLayout>
  )
}
